import { GameScene, InputState, GAME_LIST, SceneType } from '../types/game.types';
import { RenderManager } from '../core/RenderManager';
import { AudioManager } from '../core/AudioManager';

interface MainMenuCallbacks {
  switchScene: (scene: SceneType) => void;
  startGame: (gameId: string) => void;
}

export class MainMenuScene implements GameScene {
  private renderManager: RenderManager;
  private audioManager: AudioManager;
  private callbacks: MainMenuCallbacks;
  private selectedGameIndex: number = 0;
  private animationTime: number = 0;
  private keyPressDelay: number = 0;
  private cachedGridInfo: any = null;

  constructor(renderManager: RenderManager, audioManager: AudioManager, callbacks: MainMenuCallbacks) {
    this.renderManager = renderManager;
    this.audioManager = audioManager;
    this.callbacks = callbacks;
  }

  public init(): void {
    console.log('Main Menu Scene initialized');
    this.selectedGameIndex = 0;
    this.animationTime = 0;
    this.keyPressDelay = 0;
  }

  public update(deltaTime: number, input: InputState): void {
    this.animationTime += deltaTime;
    
    if (this.keyPressDelay > 0) {
      this.keyPressDelay -= deltaTime;
    }
    
    this.handleInput(input);
  }

  public render(ctx: CanvasRenderingContext2D): void {
    // Clear screen with C64 blue
    this.renderManager.clear('#40318D');

    // Get canvas dimensions
    const canvasWidth = ctx.canvas.width;
    const canvasHeight = ctx.canvas.height;

    // Draw title
    this.drawTitle(canvasWidth, canvasHeight);

    // Draw game grid
    this.drawGameGrid(canvasWidth, canvasHeight);

    // Draw footer
    this.drawFooter(canvasWidth, canvasHeight);

    // Draw instructions last (on top)
    this.drawInstructions(canvasWidth, canvasHeight);
  }

  private drawTitle(canvasWidth: number, canvasHeight: number): void {
    const centerX = canvasWidth / 2;
    const startY = canvasHeight * 0.03; // 3% from top
    const color = '#62D0FE'; // C64 light blue

    // Responsive font sizes
    const titleSize = Math.max(24, Math.min(36, canvasWidth * 0.045));
    const subtitleSize = Math.max(10, Math.min(14, canvasWidth * 0.0175));

    // Simple clean title
    this.renderManager.drawTextCentered('C64 ARCADE', centerX, startY, color, `bold ${titleSize}px "Courier New", monospace`);
    this.renderManager.drawTextCentered('CLASSIC GAMES COLLECTION', centerX, startY + titleSize + 10, '#BFCE72', `${subtitleSize}px "Courier New", monospace`);
  }

  private drawGameGrid(canvasWidth: number, canvasHeight: number): void {
    // Responsive layout - single column on narrow screens
    const isMobile = canvasWidth < 700;
    const cols = isMobile ? 1 : 3;
    const spacing = isMobile ? 10 : 15;

    // Card dimensions relative to canvas size
    const cardWidth = isMobile ? canvasWidth * 0.85 : Math.min(150, canvasWidth * 0.185);
    const cardHeight = isMobile ? canvasHeight * 0.12 : Math.min(100, canvasHeight * 0.165);

    // Center the grid
    const totalGridWidth = cols * cardWidth + (cols - 1) * spacing;
    const gridStartX = (canvasWidth - totalGridWidth) / 2;
    const gridStartY = canvasHeight * 0.15; // 15% from top
    
    GAME_LIST.forEach((game, index) => {
      const row = Math.floor(index / cols);
      const col = index % cols;
      
      const x = gridStartX + col * (cardWidth + spacing);
      const y = gridStartY + row * (cardHeight + spacing);
      
      const isSelected = index === this.selectedGameIndex;
      
      // Card background
      const bgColor = isSelected ? '#FFFFFF' : '#000000';
      const textColor = isSelected ? '#000000' : '#FFFFFF';
      const borderColor = isSelected ? '#62D0FE' : '#FFFFFF';

      this.renderManager.fillRect(x, y, cardWidth, cardHeight, bgColor);
      this.renderManager.strokeRect(x, y, cardWidth, cardHeight, borderColor, isSelected ? 3 : 2);

      // Responsive font sizes
      const titleSize = Math.max(8, Math.min(10, cardWidth * 0.065));
      const scoreSize = Math.max(7, Math.min(8, cardWidth * 0.053));

      // Game title
      this.renderManager.drawTextCentered(game.title, x + cardWidth / 2, y + titleSize + 2, textColor, `${titleSize}px "Courier New", monospace`);

      // Preview area
      const previewPadding = cardWidth * 0.065;
      const previewX = x + previewPadding;
      const previewY = y + titleSize + 15;
      const previewWidth = cardWidth - previewPadding * 2;
      const previewHeight = cardHeight - titleSize - 30;

      this.renderManager.fillRect(previewX, previewY, previewWidth, previewHeight, isSelected ? '#40318D' : '#40318D');
      this.renderManager.strokeRect(previewX, previewY, previewWidth, previewHeight, '#787878', 1);

      // Draw preview animation
      this.drawGamePreview(game.id, previewX, previewY, previewWidth, previewHeight);

      // High score
      this.renderManager.drawTextCentered(`HIGH: ${game.highScore}`, x + cardWidth / 2, y + cardHeight - scoreSize - 5, '#BFCE72', `${scoreSize}px "Courier New", monospace`);
    });

    // Store grid info for touch handling
    this.cachedGridInfo = { gridStartX, gridStartY, cardWidth, cardHeight, cols, spacing };
  }

  private drawGamePreview(gameId: string, x: number, y: number, width: number, height: number): void {
    const centerX = x + width / 2;
    const centerY = y + height / 2;
    const time = this.animationTime;
    
    switch (gameId) {
      case 'galactic-defense':
        // Draw animated space invaders
        for (let i = 0; i < 5; i++) {
          const enemyX = x + 15 + i * 20;
          const enemyY = y + 10 + Math.sin(time * 2 + i) * 3;
          this.renderManager.drawC64Char('▲', enemyX, enemyY, '#FFFFFF', 1);
        }
        // Player ship
        this.renderManager.drawC64Char('▲', centerX - 4, y + height - 15, '#62D0FE', 1);
        break;
        
      case 'maze-runner':
        // Draw maze-like pattern
        this.renderManager.drawText('███ ███', x + 20, y + 10, '#FFFFFF', '12px "Courier New", monospace');
        this.renderManager.drawText('● ● ● ●', x + 20, y + 25, '#BFCE72', '12px "Courier New", monospace');
        this.renderManager.drawText('███ ███', x + 20, y + 40, '#FFFFFF', '12px "Courier New", monospace');
        // Animated pac-man
        const pacX = x + 20 + (time * 30) % (width - 40);
        this.renderManager.drawC64Char('●', pacX, y + 25, '#BFCE72', 1);
        break;
        
      case 'river-crossing':
        // Draw road and river
        this.renderManager.fillRect(x, y + 10, width, 15, '#787878');
        this.renderManager.fillRect(x, y + 35, width, 15, '#67B6BD');
        // Animated frog
        const frogY = y + 20 + Math.sin(time * 4) * 10;
        this.renderManager.drawText('🐸', centerX - 8, frogY, '#55A049');
        break;
        
      case 'space-debris':
        // Draw rotating asteroids
        for (let i = 0; i < 4; i++) {
          const angle = time * 2 + i * Math.PI / 2;
          const asteroidX = centerX + Math.cos(angle) * 30;
          const asteroidY = centerY + Math.sin(angle) * 20;
          this.renderManager.drawC64Char('◊', asteroidX, asteroidY, '#FFFFFF', 1);
        }
        // Player ship in center
        this.renderManager.drawC64Char('▲', centerX - 4, centerY - 4, '#62D0FE', 1);
        break;
        
      case 'tunnel-blaster':
        // Draw tunnel effect
        const tunnelRadius = 20 + Math.sin(time * 3) * 5;
        this.renderManager.drawCircle(centerX, centerY, tunnelRadius, false, '#FFFFFF', 2);
        this.renderManager.drawCircle(centerX, centerY, tunnelRadius - 10, false, '#787878', 1);
        // Enemies on perimeter
        for (let i = 0; i < 3; i++) {
          const angle = time + i * Math.PI * 2 / 3;
          const enemyX = centerX + Math.cos(angle) * tunnelRadius;
          const enemyY = centerY + Math.sin(angle) * tunnelRadius;
          this.renderManager.drawPixel(enemyX, enemyY, '#883932');
        }
        break;
        
      case 'block-drop':
        // Draw falling blocks
        const blockPositions = [
          { x: x + 30, y: y + 10 + (time * 40) % 50 },
          { x: x + 50, y: y + 5 + (time * 35) % 50 },
          { x: x + 70, y: y + 15 + (time * 45) % 50 }
        ];
        blockPositions.forEach(pos => {
          this.renderManager.drawC64Char('▓', pos.x, pos.y, '#8B3F96', 1);
        });
        // Bottom filled area
        this.renderManager.fillRect(x + 10, y + height - 10, width - 20, 8, '#55A049');
        break;
        
      case 'defender':
        // Draw scrolling terrain (clipped to preview bounds)
        const terrainY = y + height - 15;
        this.renderManager.fillRect(x, terrainY, width, 15, '#8B4513'); // Brown terrain
        
        // Draw animated mountains/terrain features (properly bounded)
        for (let i = 0; i < 4; i++) {
          const terrainOffset = (time * 30) % (width + 20); // Scroll within preview width
          const terrainX = x + (i * 30) - terrainOffset;
          
          // Only draw if within preview bounds
          if (terrainX + 15 >= x && terrainX <= x + width) {
            const terrainHeight = 8 + Math.sin(i * 0.5) * 4;
            this.renderManager.fillRect(terrainX, terrainY - terrainHeight, 15, terrainHeight, '#A0522D');
          }
        }
        
        // Player ship (moving left-right within bounds)
        const shipX = centerX + Math.sin(time * 2) * (width * 0.3);
        this.renderManager.drawC64Char('▲', shipX - 4, y + 35, '#00FF00', 1);
        
        // Enemies (landers) - bounded to preview area
        for (let i = 0; i < 3; i++) {
          const enemyX = x + 15 + ((i * 25 + time * 20) % (width - 30));
          const enemyY = y + 15 + Math.sin(time * 3 + i) * 6;
          this.renderManager.drawC64Char('■', enemyX, enemyY, '#FF0000', 1);
        }
        
        // Humanoids on ground (within bounds)
        for (let i = 0; i < 2; i++) {
          const humanX = x + 20 + i * (width * 0.4);
          this.renderManager.drawC64Char('♠', humanX, terrainY - 8, '#FFFF00', 1);
        }
        break;
        
      case 'centipede':
        // Draw mushroom field
        for (let i = 0; i < 8; i++) {
          const mushroomX = x + 15 + (i * 15) % (width - 30);
          const mushroomY = y + 20 + (i % 3) * 15;
          this.renderManager.drawC64Char('♣', mushroomX, mushroomY, '#FF69B4', 1); // Pink mushrooms
        }
        
        // Animated centipede segments moving in wave pattern
        const segmentCount = 8;
        for (let i = 0; i < segmentCount; i++) {
          const waveOffset = Math.sin(time * 2 + i * 0.3) * 8;
          const segmentX = x + 10 + (i * 8 + time * 20) % (width - 20);
          const segmentY = y + 10 + waveOffset;
          
          // Head segment is different color
          const color = i === 0 ? '#00FF00' : '#FFFF00';
          this.renderManager.drawC64Char('●', segmentX, segmentY, color, 1);
        }
        
        // Player ship at bottom
        this.renderManager.drawC64Char('▲', centerX - 4, y + height - 12, '#62D0FE', 1);
        
        // Animated bullet
        const bulletY = y + height - 20 - (time * 60) % 40;
        this.renderManager.drawC64Char('•', centerX - 2, bulletY, '#FFFFFF', 1);
        break;
        
      case 'breakout':
        // Draw brick rows
        const brickColors = ['#FF0000', '#FF6600', '#FFFF00', '#00FF00'];
        for (let row = 0; row < 4; row++) {
          for (let col = 0; col < 6; col++) {
            const brickX = x + 5 + col * 20;
            const brickY = y + 5 + row * 8;
            this.renderManager.fillRect(brickX, brickY, 18, 6, brickColors[row]);
            this.renderManager.strokeRect(brickX, brickY, 18, 6, '#FFFFFF', 1);
          }
        }

        // Animated paddle
        const paddleX = centerX + Math.sin(time * 3) * 30 - 15;
        this.renderManager.fillRect(paddleX, y + height - 10, 30, 4, '#00AAFF');

        // Bouncing ball
        const ballX = centerX + Math.sin(time * 4) * 40;
        const ballY = y + 25 + Math.abs(Math.sin(time * 6)) * 15;
        this.renderManager.drawCircle(ballX, ballY, 3, true, '#FFFFFF', 1);
        break;

      default:
        // Default animation for unknown games
        this.renderManager.drawTextCentered('?', centerX, centerY - 4, '#FFFFFF', '24px "Courier New", monospace');
        break;
    }
  }

  private drawInstructions(canvasWidth: number, canvasHeight: number): void {
    const centerX = canvasWidth / 2;
    const y = canvasHeight * 0.78; // 78% from top
    const boxHeight = canvasHeight * 0.22; // 22% of height

    // Responsive font sizes
    const mainTextSize = Math.max(9, Math.min(11, canvasWidth * 0.0135));
    const subTextSize = Math.max(8, Math.min(10, canvasWidth * 0.0125));

    // Add a background box to make instructions more visible
    this.renderManager.fillRect(0, y - 10, canvasWidth, boxHeight, 'rgba(0, 0, 0, 0.7)');

    const isMobile = canvasWidth < 700;

    if (isMobile) {
      this.renderManager.drawTextCentered('TAP TO SELECT', centerX, y, '#FFFFFF', `${mainTextSize}px "Courier New", monospace`);
      this.renderManager.drawTextCentered('TAP AGAIN TO START', centerX, y + 18, '#BFCE72', `${subTextSize}px "Courier New", monospace`);
    } else {
      this.renderManager.drawTextCentered('USE ARROW KEYS OR WASD TO SELECT GAME', centerX, y, '#FFFFFF', `${mainTextSize}px "Courier New", monospace`);
      this.renderManager.drawTextCentered('PRESS SPACE OR ENTER TO START • PRESS L FOR LEADERBOARD', centerX, y + 18, '#BFCE72', `${subTextSize}px "Courier New", monospace`);
    }

    // Show selected game controls
    const selectedGame = GAME_LIST[this.selectedGameIndex];
    if (selectedGame) {
      const controlsText = selectedGame.controls.join(' • ');
      this.renderManager.drawTextCentered(`CONTROLS: ${controlsText}`, centerX, y + 36, '#62D0FE', `${subTextSize}px "Courier New", monospace`);
    }
  }

  private drawFooter(canvasWidth: number, canvasHeight: number): void {
    const centerX = canvasWidth / 2;
    const y = canvasHeight * 0.63; // 63% from top

    // Responsive font sizes
    const readySize = Math.max(12, Math.min(14, canvasWidth * 0.0175));
    const footerSize = Math.max(7, Math.min(8, canvasWidth * 0.01));

    this.renderManager.drawTextCentered('READY.', centerX, y, '#55A049', `${readySize}px "Courier New", monospace`);

    // Blinking cursor
    if (Math.floor(this.animationTime * 2) % 2 === 0) {
      this.renderManager.drawText('█', centerX + 30, y, '#55A049', `${readySize}px "Courier New", monospace`);
    }

    // Footer info
    this.renderManager.drawTextCentered('C64 ARCADE • 2024', centerX, y + 25, '#787878', `${footerSize}px "Courier New", monospace`);
  }

  public handleInput(input: InputState): void {
    if (this.keyPressDelay > 0) return;
    
    const movement = this.getMenuMovement(input);
    
    if (movement.x !== 0 || movement.y !== 0) {
      const cols = 3;
      const currentRow = Math.floor(this.selectedGameIndex / cols);
      const currentCol = this.selectedGameIndex % cols;
      
      let newRow = currentRow + movement.y;
      let newCol = currentCol + movement.x;
      
      // Wrap around
      const maxRow = Math.ceil(GAME_LIST.length / cols) - 1;
      if (newRow < 0) newRow = maxRow;
      if (newRow > maxRow) newRow = 0;
      if (newCol < 0) newCol = cols - 1;
      if (newCol >= cols) newCol = 0;
      
      const newIndex = newRow * cols + newCol;
      
      // Make sure we don't go beyond available games
      if (newIndex < GAME_LIST.length) {
        this.selectedGameIndex = newIndex;
        this.audioManager.playMenuMove();
        this.keyPressDelay = 0.15; // Prevent rapid scrolling
      }
    }
    
    // Handle selection
    if (input.keys['Space'] || input.keys['Enter']) {
      const selectedGame = GAME_LIST[this.selectedGameIndex];
      this.audioManager.playMenuSelect();
      console.log(`Selected game: ${selectedGame.title}`);
      
      // Start the selected game
      this.callbacks.startGame(selectedGame.id);
      this.keyPressDelay = 0.3;
    }
    
    // Handle tap selection - check if tap is on a game card
    if (input.touch.gesture === 'tap' && this.cachedGridInfo) {
      const tapX = input.touch.position.x;
      const tapY = input.touch.position.y;

      const { gridStartX, gridStartY, cardWidth, cardHeight, cols, spacing } = this.cachedGridInfo;

      let tappedGameIndex = -1;

      GAME_LIST.forEach((game, index) => {
        const row = Math.floor(index / cols);
        const col = index % cols;

        const x = gridStartX + col * (cardWidth + spacing);
        const y = gridStartY + row * (cardHeight + spacing);

        // Check if tap is within this card
        if (tapX >= x && tapX <= x + cardWidth && tapY >= y && tapY <= y + cardHeight) {
          tappedGameIndex = index;
        }
      });

      if (tappedGameIndex !== -1) {
        if (tappedGameIndex === this.selectedGameIndex) {
          // Double-tap or tap on already selected - start game
          const selectedGame = GAME_LIST[this.selectedGameIndex];
          this.audioManager.playMenuSelect();
          console.log(`Tapped to start game: ${selectedGame.title}`);
          this.callbacks.startGame(selectedGame.id);
        } else {
          // Single tap - select game
          this.selectedGameIndex = tappedGameIndex;
          this.audioManager.playMenuMove();
          console.log(`Tapped to select game: ${GAME_LIST[this.selectedGameIndex].title}`);
        }
        this.keyPressDelay = 0.3;
      }
    }
    
    // Handle leaderboard
    if (input.keys['KeyL']) {
      this.audioManager.playMenuSelect();
      this.callbacks.switchScene(SceneType.LEADERBOARD);
      this.keyPressDelay = 0.3;
    }
  }

  private getMenuMovement(input: InputState): { x: number, y: number } {
    const movement = { x: 0, y: 0 };
    
    // Keyboard input
    if (input.keys['ArrowLeft'] || input.keys['KeyA']) {
      movement.x = -1;
    } else if (input.keys['ArrowRight'] || input.keys['KeyD']) {
      movement.x = 1;
    }
    
    if (input.keys['ArrowUp'] || input.keys['KeyW']) {
      movement.y = -1;
    } else if (input.keys['ArrowDown'] || input.keys['KeyS']) {
      movement.y = 1;
    }
    
    // Touch input (swipe gestures)
    if (input.touch.gesture === 'swipe') {
      const swipeDir = this.getSwipeDirection(input);
      if (Math.abs(swipeDir.x) > Math.abs(swipeDir.y)) {
        movement.x = swipeDir.x > 0 ? 1 : -1;
      } else {
        movement.y = swipeDir.y > 0 ? 1 : -1;
      }
    }
    
    return movement;
  }

  private getSwipeDirection(input: InputState): { x: number, y: number } {
    if (input.touch.gesture === 'swipe') {
      const deltaX = input.touch.position.x - input.touch.startPosition.x;
      const deltaY = input.touch.position.y - input.touch.startPosition.y;
      
      const magnitude = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
      if (magnitude > 0) {
        return { x: deltaX / magnitude, y: deltaY / magnitude };
      }
    }
    
    return { x: 0, y: 0 };
  }

  public cleanup(): void {
    console.log('Main Menu Scene cleaned up');
  }
}